import { GroupsOutlined as GroupsOutlinedIcon } from 'components/icons';
import PermissionButton from 'components/buttons/PermissionButton';
import EmptyState from 'components/EmptyState';
import { DataTable, useDataTableSortString } from 'components/tables/data-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { sortByAccessor } from 'components/tables/list-table/sortUtil';
import { useRowLink } from 'components/tables/list-table/useRowLink';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { TeamEmployee } from 'types/teams';
import { t } from 'utils/getTranslations';
import { formatEmployeeName, useLastNameFirst } from 'utils/nameOrder';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';

interface Props {
    teamEmployees: TeamEmployee[];
}

const DEFAULT_SORT = 'name,asc';

const getEmployeeHref = (employee: TeamEmployee) => `/employee/${employee.id}`;

const getEmployeeName = (employee: TeamEmployee, lastNameFirst: boolean): string =>
    formatEmployeeName(employee, lastNameFirst) || '-';

const getMembershipTypeLabel = (employee: TeamEmployee): string => {
    switch (employee.teamMembershipType) {
        case 'CHAIRMAN':
            return t('teams.membership-type-chairman');
        case 'SUPPORT':
            return t('teams.membership-type-support');
        default:
            return '-';
    }
};

// Accessor used both for the sortable cell value and the client-side sort comparator,
// keyed by column id so a header click drives the in-memory sort below. The `name` accessor
// reflects LAST_NAME_FIRST, so sorting by it orders by last name when the flag is on.
const buildSortAccessors = (lastNameFirst: boolean): Record<string, (employee: TeamEmployee) => string> => ({
    name: (employee) => getEmployeeName(employee, lastNameFirst),
    teamMembershipType: getMembershipTypeLabel,
    'office.name': (employee) => employee.office?.name ?? '-',
    isCoreEmployee: (employee) => (employee.isCoreEmployee ? t('yes') : t('no')),
});

const buildColumns = (
    navigateWithReturn: ReturnType<typeof useNavigateWithReturnState>,
    lastNameFirst: boolean
): DataTableColumnDef<TeamEmployee>[] => [
    {
        header: t('teams.team-employees'),
        id: 'name',
        accessorFn: (row) => getEmployeeName(row, lastNameFirst),
        meta: {
            widthWeight: 1,
        },
        cell: ({ row }) => (
            <RouterLink
                className="entity-link text-fg hover:text-link font-semibold no-underline hover:underline"
                to={`/employee/${row.original.id}`}
                onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
                        return;
                    }
                    event.preventDefault();
                    navigateWithReturn(`/employee/${row.original.id}`);
                }}
            >
                {getEmployeeName(row.original, lastNameFirst)}
            </RouterLink>
        ),
    },
    {
        header: t('teams.team-membership-type'),
        id: 'teamMembershipType',
        accessorFn: (row) => getMembershipTypeLabel(row),
        meta: {
            widthWeight: 1,
        },
    },
    {
        header: t('office'),
        id: 'office.name',
        accessorFn: (row) => row.office?.name ?? '-',
        meta: {
            widthWeight: 1,
        },
    },
    {
        header: t('teams.is-core-employee'),
        id: 'isCoreEmployee',
        accessorFn: (row) => (row.isCoreEmployee ? t('yes') : t('no')),
        meta: {
            widthWeight: 1,
        },
    },
];

const EmployeesView: React.FC<Props> = ({ teamEmployees }) => {
    const navigateWithReturn = useNavigateWithReturnState();
    const rowLink = useRowLink(getEmployeeHref);
    const lastNameFirst = useLastNameFirst();
    const [sort, setSort] = useState(DEFAULT_SORT);

    const columns = useMemo<DataTableColumnDef<TeamEmployee>[]>(
        () => buildColumns(navigateWithReturn, lastNameFirst),
        [navigateWithReturn, lastNameFirst]
    );

    // The table runs in manual-sort mode, so it never reorders rows itself — it only emits the
    // requested sort. We feed it pre-sorted in-memory data (already returned by GET /team/:id).
    const sortedEmployees = useMemo(() => {
        const [accessorId, direction] = sort.split(',');
        const sortAccessors = buildSortAccessors(lastNameFirst);
        const accessor = sortAccessors[accessorId] ?? sortAccessors.name;
        return sortByAccessor(teamEmployees, accessor, direction === 'desc');
    }, [teamEmployees, sort, lastNameFirst]);

    const applySort = useCallback((newSort: string) => setSort(newSort || DEFAULT_SORT), []);
    const { sorting, onSortingChange } = useDataTableSortString(sort, applySort, columns);

    return (
        <div className="flex h-full flex-col gap-4">
            <DataTable<TeamEmployee>
                columns={columns}
                data={sortedEmployees}
                {...rowLink}
                columnConfigId="team-employees"
                sorting={sorting}
                onSortingChange={onSortingChange}
                // Membership is set from the colleague's own profile, not here, so the CTA is
                // navigational rather than a create action.
                emptyState={
                    <EmptyState
                        size="page"
                        icon={GroupsOutlinedIcon}
                        titleId="teams.empty.employees-title"
                        bodyId="teams.empty.employees-body"
                        surface="team-employees"
                        action={
                            <PermissionButton
                                permissions={['READ.EMPLOYEES']}
                                variant="contained"
                                color="secondary"
                                onClick={() => navigateWithReturn('/employees')}
                            >
                                <FormattedMessage id="employees.go-to-colleagues" />
                            </PermissionButton>
                        }
                    />
                }
            />
        </div>
    );
};

export default EmployeesView;
